<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="true"
    :draggable="false"
    :dismissable-mask="false"
    class="whatsapp-ctas-modal"
    :style="{ width: '900px', maxWidth: '95vw' }"
    @update:visible="handleClose"
  >
    <template #header>
      <span class="modal-title font-semibold">
        {{ t('templateBuilder.fields.ctaConfiguration') }}
      </span>
    </template>

    <div class="whatsapp-ctas-modal__content">
      <div
        v-if="limitReachedMessage"
        class="whatsapp-ctas-modal__warning mb-3"
      >
        <i class="pi pi-exclamation-triangle mr-2" />
        {{ limitReachedMessage }}
      </div>

      <div class="whatsapp-ctas-modal__grid">
        <div
          v-for="(cta, index) in localCtasData"
          :key="`${cta.translationId}-${index}`"
          class="whatsapp-ctas-modal__card"
        >
          <div class="whatsapp-ctas-modal__card-header">
            <span class="whatsapp-ctas-modal__card-title">
              {{ t('templateBuilder.whatsapp.ctaNumber', { number: index + 1 }) }}
            </span>
            <SpmButton
              icon="fa-regular fa-trash"
              class-style="p-button p-button-danger p-button-text p-button-sm"
              @click="deleteCta(index)"
            />
          </div>
          <div class="whatsapp-ctas-modal__card-body">
            <WhatsappCtaItem
              :model-value="cta.value"
              :action="cta.action"
              :cta-type-options="ctaTypeOptions"
              :disabled-options="getDisabledOptionsForCta(index)"
              :configs="configs"
              :id-campaign="idCampaign"
              :origin-call="originCall"
              @update:model-value="(newValue) => updateCtaValue(index, newValue)"
              @update:action="(newAction) => updateCtaAction(index, newAction)"
            />
          </div>
        </div>

        <div
          class="whatsapp-ctas-modal__card whatsapp-ctas-modal__card--add"
          :class="{ 'whatsapp-ctas-modal__card--disabled': isAddButtonDisabled }"
          @click="!isAddButtonDisabled && addCta()"
        >
          <div class="whatsapp-ctas-modal__add-content">
            <i class="fa-regular fa-plus whatsapp-ctas-modal__add-icon" />
            <span>{{ t('templateBuilder.whatsapp.addCta') }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer flex justify-content-end gap-2">
        <SpmButton
          :label="t('common.close')"
          icon="far fa-times"
          class-style="p-button-secondary p-button-sm"
          @click="handleClose"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { LinkTypeEnum, ParserFieldObject, Property } from '@/types';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import WhatsappCtaItem from '@/components/template-builder/fields/WhatsappCtaItem.vue';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { useI18n } from 'vue-i18n';
import { store } from '@/store';
import { createTranslationIdentifier } from '../utils/translate';
import { isWhatsappSmartListEnabled } from '../getters/whatsapp-smart-product-list';

interface CtaData {
  translationId: string | null;
  value: string;
  action: {
    type: string;
    value: string;
  };
}

export default defineComponent({
  name: 'WhatsappCtasModal',

  components: {
    Dialog,
    SpmButton,
    WhatsappCtaItem,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    configs: {
      type: Object,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'update:visible': Boolean,
    'on-change-properties': Object,
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const localCtasData = ref<CtaData[]>([]);

    const ctaTypeOptions = computed(() => props.configs.options.ctaTypeOptions);

    // Count buttons by type
    const buttonCounts = computed(() => {
      const counts = {
        callNumber: 0,
        webAddress: 0,
        total: localCtasData.value.length,
      };

      localCtasData.value.forEach((cta) => {
        if (cta.action.type === LinkTypeEnum.CALL_NUMBER) {
          counts.callNumber += 1;
        } else if (cta.action.type === LinkTypeEnum.WEB_ADDRESS) {
          counts.webAddress += 1;
        }
      });

      return counts;
    });

    // Check if add button should be disabled
    const isAddButtonDisabled = computed(() => {
      if (isWhatsappSmartListEnabled() && buttonCounts.value.total >= 1) {
        return true;
      }
      if (buttonCounts.value.total >= 3) {
        return true;
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return true;
      }
      return false;
    });

    // Generate appropriate limit message
    const limitReachedMessage = computed(() => {
      if (isWhatsappSmartListEnabled() && buttonCounts.value.total >= 1) {
        return t('templateBuilder.whatsapp.maxButtonsSmartlist');
      }
      if (buttonCounts.value.total >= 3) {
        return t('templateBuilder.whatsapp.maxButtonsReached');
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return t('templateBuilder.whatsapp.maxButtonTypesReached');
      }
      return null;
    });

    const originCall = computed(() => {
      let returnValue = 'templateBuilder';

      if (TemplateEditorState.template?.createdFromCampaign) {
        if ((new RegExp('_automation')).test(TemplateEditorState.template?.createdFromCampaign)) {
          returnValue = 'automation';
        } else {
          returnValue = 'newsletter';
        }
      }
      return returnValue;
    });

    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? undefined);

    // Watch for visibility changes to sync data when modal opens
    watch(
      () => props.visible,
      (isVisible) => {
        if (isVisible) {
          // Sync data from props when modal opens
          const propValue = props.parserValues.properties[0].value;
          if (Array.isArray(propValue)) {
            localCtasData.value = JSON.parse(JSON.stringify(propValue));
          }
        }
      },
      { immediate: true },
    );

    const emitChanges = () => {
      const updatedProperty: Property = {
        ...props.parserValues.properties[0],
        value: localCtasData.value,
      };
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [updatedProperty],
      });
    };

    const addCta = () => {
      if (!isAddButtonDisabled.value) {
        localCtasData.value.push({
          translationId: createTranslationIdentifier(),
          value: t('templateBuilder.whatsapp.newAction'),
          action: {
            type: LinkTypeEnum.QUICK_REPLY,
            value: 'reply:quick_close',
          },
        });
        emitChanges();
      }
    };

    const deleteCta = (index: number) => {
      if (localCtasData.value.length > 0 && index >= 0 && index < localCtasData.value.length) {
        localCtasData.value.splice(index, 1);
        emitChanges();
      }
    };

    const updateCtaValue = (index: number, newValue: string) => {
      if (index >= 0 && index < localCtasData.value.length) {
        localCtasData.value[index].value = newValue;
        emitChanges();
      }
    };

    const updateCtaAction = (index: number, newAction: { type: string; value: string }) => {
      if (index >= 0 && index < localCtasData.value.length) {
        localCtasData.value[index].action = { ...newAction };
        emitChanges();
      }
    };

    const getDisabledOptionsForCta = (ctaIndex: number): LinkTypeEnum[] => {
      const currentCta = localCtasData.value[ctaIndex];
      const currentType = currentCta?.action.type;
      const disabledOptions: LinkTypeEnum[] = [];

      ctaTypeOptions.value.forEach((option: string) => {
        if (option === currentType) {
          return;
        }

        if (option === LinkTypeEnum.CALL_NUMBER && buttonCounts.value.callNumber >= 1) {
          disabledOptions.push(option as LinkTypeEnum);
        } else if (option === LinkTypeEnum.WEB_ADDRESS && buttonCounts.value.webAddress >= 2) {
          disabledOptions.push(option as LinkTypeEnum);
        }
      });

      return disabledOptions;
    };

    const handleClose = () => {
      emit('update:visible', false);
      // Refresh configuration panel to update smartlist disable state based on CTA count
      store.commit('liveEditor/refreshConfigurationPanelKey');
    };

    return {
      t,
      localCtasData,
      ctaTypeOptions,
      idCampaign,
      originCall,
      isAddButtonDisabled,
      limitReachedMessage,
      addCta,
      deleteCta,
      updateCtaValue,
      updateCtaAction,
      getDisabledOptionsForCta,
      handleClose,
    };
  },
});
</script>

<style lang="scss">
.whatsapp-ctas-modal {
  &__content {
    padding: 0.5rem;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  &__card {
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 8px;
    background: var(--surface-card, #fff);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &--add {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      border-style: dashed;
      cursor: pointer;
      background: var(--surface-ground, #f8f9fa);

      &:hover:not(.whatsapp-ctas-modal__card--disabled) {
        background: var(--surface-hover, #e9ecef);
        border-color: var(--primary-color, #3b82f6);
      }
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: var(--surface-border, #dee2e6);
      background: var(--surface-ground, #f8f9fa);

      .whatsapp-ctas-modal__add-content {
        color: var(--text-color-secondary, #9ca3af);
      }
    }
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: var(--surface-ground, #f8f9fa);
    border-bottom: 1px solid var(--surface-border, #dee2e6);
  }

  &__card-title {
    font-weight: 600;
    color: var(--text-color, #495057);
  }

  &__card-body {
    padding: 1rem;
  }

  &__add-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-color-secondary, #6c757d);
  }

  &__add-icon {
    font-size: 2rem;
  }

  &__warning {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 6px;
    color: #c2410c;
    font-size: 0.875rem;
  }
}
</style>
